import { useState } from "react";
import { motion } from "framer-motion";

export type MascotPose =
  | "idle"
  | "wave"
  | "thumbs-up"
  | "point"
  | "peek";

interface MascotProps {
  pose?: MascotPose;
  /** px height */
  size?: number;
  flip?: boolean;
  /** gentle continuous bob */
  bob?: boolean;
  className?: string;
  alt?: string;
}

/**
 * The Provenduz mascot as a lightweight 2D sprite. Poses are plain PNGs under
 * /public/mascot/<pose>.png — a missing file just renders nothing (no broken
 * image icon). Falls back to `idle.png` when a specific pose is absent.
 */
export const Mascot = ({
  pose = "idle",
  size = 140,
  flip = false,
  bob = true,
  className = "",
  alt = "Mascote da Provenduz",
}: MascotProps) => {
  const [src, setSrc] = useState(`/mascot/${pose}.png`);
  const [broken, setBroken] = useState(false);

  if (broken) return null;

  return (
    <motion.img
      src={src}
      alt={alt}
      draggable={false}
      className={`mascot ${className}`}
      style={{
        height: size,
        width: "auto",
        transform: flip ? "scaleX(-1)" : undefined,
        pointerEvents: "none",
        userSelect: "none",
      }}
      onError={() => {
        if (src.endsWith(`${pose}.png`) && pose !== "idle") {
          setSrc("/mascot/idle.png");
        } else {
          setBroken(true);
        }
      }}
      animate={
        bob
          ? { y: [0, -8, 0], rotate: flip ? [0, 1.5, 0] : [0, -1.5, 0] }
          : undefined
      }
      transition={
        bob
          ? { duration: 3.4, repeat: Infinity, ease: "easeInOut" }
          : undefined
      }
    />
  );
};
